import { useEffect, useMemo, useRef, useState } from "react";
import { QuoteApi, QuoteSubmitFailureMapper } from "./api";
import { applyExtracted, type UnsupportedRouteWarning } from "./applyExtracted";
import { ExtractFillMap } from "./extractFill";
import { ChoosePath } from "./components/ChoosePath";
import { QuickFill } from "./components/QuickFill";
import { QuoteForm } from "./components/QuoteForm";
import { ResultStep } from "./components/ResultStep";
import { ReviewStep } from "./components/ReviewStep";
import { StepIndicator } from "./components/StepIndicator";
import { DepartmentFieldAccess, DepartmentFormDefaults } from "./departmentFields";
import { QuoteCopy } from "./i18n/QuoteCopy";
import { QuoteCopyProvider } from "./i18n/QuoteCopyContext";
import { ThankYouRedirect } from "./i18n/ThankYouRedirect";
import type { CopyLang } from "./i18n/copyTypes";
import { makeInitialState } from "./formModel";
import {
  useDepartmentConfig,
  useGatedErrors,
  useInvalidFieldFocus,
  useStepAnnouncement,
  useTransientHighlight,
} from "./hooks";
import { parseText } from "./parser";
import { QuotePathResolver, type QuoteNav } from "./quotePath";
import { QuoteWidgetRoot } from "./quoteWidgetRoot";
import { QuoteDataMapper } from "./quotePayload";
import { buildSubject, generateEmailText } from "./summary";
import type { ExtractedData, FormState, Outcome, View, RequestPath, ManualPage } from "./types";
import {
  firstInvalidFieldId,
  firstInvalidPage,
  pickPageErrors,
  validateContact,
  validateForm,
  validateQuoteText,
} from "./validation";

const quotePath = new QuotePathResolver();
const quoteData = new QuoteDataMapper();
const submitFailure = new QuoteSubmitFailureMapper();
const thankYouRedirect = new ThankYouRedirect();

interface Props {
  department: string;
  apiBase: string;
  language: CopyLang;
}

export default function App({ department, apiBase, language }: Props) {
  const api = useMemo(() => new QuoteApi(apiBase), [apiBase]);
  const config = useDepartmentConfig(api, department);
  const copy = useMemo(() => QuoteCopy.forLanguage(language), [language]);

  const [form, setForm] = useState<FormState>(makeInitialState);

  useEffect(() => {
    if (!config) return;
    setForm((current) => new DepartmentFormDefaults(new DepartmentFieldAccess(config)).apply(current));
  }, [config]);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [qfText, setQfText] = useState("");
  const [qfFiles, setQfFiles] = useState<File[]>([]);
  const [manualPage, setManualPage] = useState<ManualPage>("route");
  const [extractFill, setExtractFill] = useState(() => new ExtractFillMap());
  const [unsupportedRoute, setUnsupportedRoute] = useState<UnsupportedRouteWarning | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const extractSeq = useRef(0);
  const submittingRef = useRef(false);

  const [viewOverride, setViewOverride] = useState<View | null>(null);
  const [pathOverride, setPathOverride] = useState<RequestPath | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  const start = config ? quotePath.start(config.flow) : null;
  const view = viewOverride ?? start?.view ?? "choose";
  const requestPath = pathOverride ?? start?.path ?? "manual";

  const { highlighted, flash, reset: resetHighlight } = useTransientHighlight();

  const routeWarning = useMemo(() => {
    if (!unsupportedRoute || !config) return null;
    const home = new DepartmentFieldAccess(config).homeCountry();
    const load = form.loadCountry.trim().toUpperCase();
    const unload = form.unloadCountry.trim().toUpperCase();
    if (!home || load === home || unload === home) return null;
    return unsupportedRoute;
  }, [unsupportedRoute, config, form.loadCountry, form.unloadCountry]);
  const allErrors = useMemo(
    () =>
      config
        ? {
            ...validateForm(form, config, copy, routeWarning),
            ...validateContact(form, copy),
            ...validateQuoteText(form, copy),
          }
        : {},
    [form, config, copy, routeWarning],
  );
  const visibleErrors = useGatedErrors(
    view === "form" ? pickPageErrors(allErrors, manualPage) : allErrors,
    submitAttempted,
    touched,
  );
  const { announce, setAnnounce } = useStepAnnouncement(
    rootRef,
    view,
    outcome,
    manualPage,
    copy,
    pendingFocusId !== null,
  );
  useInvalidFieldFocus(rootRef, pendingFocusId, () => setPendingFocusId(null));

  useEffect(() => () => thankYouRedirect.cancel(), []);

  const markTouched = (id: string) => setTouched((s) => (s.has(id) ? s : new Set(s).add(id)));

  const applyNav = (nav: QuoteNav) => {
    if (nav.page) setManualPage(nav.page);
    setViewOverride(nav.view);
  };

  const resetSession = (path: RequestPath | null, nextView: View | null) => {
    thankYouRedirect.cancel();
    extractSeq.current += 1;
    submittingRef.current = false;
    setSessionKey((key) => key + 1);
    setForm(makeInitialState(config ?? undefined));
    setExtractFill(new ExtractFillMap());
    setUnsupportedRoute(null);
    setPathOverride(path);
    setManualPage("route");
    setViewOverride(nextView);
    setTouched(new Set());
    setSubmitAttempted(false);
    resetHighlight();
    setQfText("");
    setQfFiles([]);
  };

  const goHome = () => resetSession(null, null);

  const leaveQuickFill = () => {
    extractSeq.current += 1;
    if (!config) {
      goHome();
      return;
    }
    const nav = quotePath.leaveQuickFill(config.flow, extractFill.hasFilled());
    if (nav) applyNav(nav);
    else goHome();
  };

  const handleExtract = async (text: string, files: File[]): Promise<string[]> => {
    const seq = ++extractSeq.current;
    let data: ExtractedData;
    if (!api.configured) {
      data = parseText(text);
    } else {
      try {
        data = await api.extract(department, text, files);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "";
        if (msg === "extraction_unavailable" && text.trim() && files.length === 0) {
          data = parseText(text);
        } else {
          throw err;
        }
      }
    }
    if (seq !== extractSeq.current) throw new Error("extract_cancelled");
    const applied = applyExtracted(
      data,
      makeInitialState(config ?? undefined),
      config ?? undefined,
    );
    const warning = applied.unsupportedRoute;
    if (applied.filled.length === 0 && !warning) return applied.filled;
    setForm(applied.state);
    setExtractFill(new ExtractFillMap(applied.filledIds, applied.missingIds));
    setUnsupportedRoute(warning);
    setTouched(new Set());
    setSubmitAttempted(false);
    flash(applied.filledIds);
    setManualPage("route");
    setViewOverride("form");
    if (applied.filled.length > 0) return applied.filled;
    if (!warning) return applied.filled;
    return [warning.loadCountry, warning.unloadCountry];
  };

  const handleSubmit = () => {
    const hasErrors = Object.keys(pickPageErrors(allErrors, manualPage)).length > 0;
    setSubmitAttempted(hasErrors);
    if (!hasErrors) {
      applyNav(quotePath.continueFrom(manualPage));
      return;
    }
    setAnnounce(copy.t("announce.completeFields"));
    const errorId = firstInvalidFieldId(allErrors, manualPage);
    if (errorId) setPendingFocusId(errorId);
  };

  const submitQuote = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const attempt = (outcome?.attempt ?? 0) + 1;
      // Snapshot so the confirmation preview shows exactly what was sent.
      const sentSubject = buildSubject(form);
      const sentBody = generateEmailText(form);

      if (!api.configured) {
        setOutcome({
          status: "success",
          refId: null,
          emailSent: false,
          demo: true,
          demoDroppedFiles: quoteData.activeFiles(form).length > 0,
          attempt,
          sentSubject,
          sentBody,
        });
        setViewOverride("result");
        thankYouRedirect.go(language);
        return;
      }

      setAnnounce(copy.t("announce.sending"));
      try {
        const result = await api.submitQuote(
          {
            department,
            contact: { name: form.contactName, company: form.company, email: form.email, phone: form.phone },
            data: quoteData.toPayload(form),
            subject: sentSubject,
            emailText: sentBody,
          },
          quoteData.activeFiles(form),
        );
        setOutcome({
          status: "success",
          refId: result.refId,
          emailSent: result.emailSent,
          attempt,
          sentSubject,
          sentBody,
        });
        setViewOverride("result");
        thankYouRedirect.go(language);
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "";
        setOutcome({
          status: "error",
          refId: null,
          emailSent: false,
          errorKind: submitFailure.kind(msg),
          attempt,
        });
      }
      setViewOverride("result");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    if (submittingRef.current || submitting) return;
    if (!config) return;
    if (Object.keys(allErrors).length > 0) {
      setSubmitAttempted(true);
      const page = firstInvalidPage(allErrors);
      const errorId = firstInvalidFieldId(allErrors);
      if (errorId) setPendingFocusId(errorId);
      if (page) {
        setManualPage(page);
        setViewOverride("form");
      }
      return;
    }
    void submitQuote();
  };

  const handleRetry = () => {
    if (submitting || submittingRef.current) return;
    void submitQuote();
  };

  const handleFinish = () => {
    goHome();
    setOutcome(null);
  };

  if (!config) {
    return <div className={QuoteWidgetRoot.className(null)}>{copy.t("common.loading")}</div>;
  }

  const showSteps = view === "form" || view === "review" || view === "result";
  const backLabel = copy.t(quotePath.backLabelKey(config.flow, view, manualPage));

  return (
    <QuoteCopyProvider value={copy}>
    <div className={QuoteWidgetRoot.className(view, manualPage)} ref={rootRef}>
      {showSteps && <StepIndicator steps={copy.stepLabels()} current={quotePath.stepIndex(manualPage, view)} />}
      <div className="nv-sr-only" aria-live="polite">
        {announce}
      </div>

      <div className="nv-shell nv-anim" key={`${sessionKey}-${view}-${manualPage}`}>
        {view === "choose" && (
          <ChoosePath
            paths={quotePath.pathsFor(config.flow)}
            onChoose={(path) => resetSession(path, quotePath.viewForPath(path))}
          />
        )}

        {view === "form" && (
          <QuoteForm
            form={form}
            setForm={setForm}
            config={config}
            errors={visibleErrors}
            submitAttempted={submitAttempted}
            highlighted={highlighted}
            extractFill={requestPath === "quickfill" ? extractFill : undefined}
            unsupportedRoute={routeWarning}
            page={manualPage}
            onBlur={markTouched}
            onBack={() => {
              setSubmitAttempted(false);
              applyNav(quotePath.backFrom(manualPage, requestPath));
            }}
            onContinue={handleSubmit}
            backLabel={backLabel}
          />
        )}

        {view === "quickfill" && (
          <QuickFill
            text={qfText}
            files={qfFiles}
            onTextChange={setQfText}
            onFilesChange={setQfFiles}
            onExtract={handleExtract}
            onBack={leaveQuickFill}
            backLabel={backLabel}
          />
        )}

        {view === "review" && (
          <ReviewStep
            form={form}
            config={config}
            submitting={submitting}
            onBack={() => {
              setSubmitAttempted(false);
              setManualPage("contact");
              setViewOverride("form");
            }}
            onConfirm={handleConfirm}
          />
        )}

        {view === "result" && outcome && (
          <ResultStep
            form={form}
            config={config}
            outcome={outcome}
            submitting={submitting}
            onStartOver={handleFinish}
            onRetry={handleRetry}
            onBackToEdit={() => setViewOverride("review")}
          />
        )}
      </div>
    </div>
    </QuoteCopyProvider>
  );
}
